import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { bookingApi, businessApi } from '../services/api';
import { Booking, Business } from '../types';
import { Modal } from '../components/Modal';

const statusColors: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
  COMPLETED: 'bg-blue-100 text-blue-800',
  NO_SHOW: 'bg-gray-100 text-gray-800',
};

export function Bookings() {
  const [searchParams] = useSearchParams();
  const initialBusinessId = searchParams.get('businessId') || '';
  
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState(initialBusinessId);
  const [isLoading, setIsLoading] = useState(true);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [businessesRes, bookingsRes] = await Promise.all([
        businessApi.getMyBusinesses(),
        bookingApi.getMyBookings({ limit: 50 }),
      ]);

      setBusinesses(businessesRes.data.data.businesses || []);
      setBookings(bookingsRes.data.data.bookings || []);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!editingBooking || !newStatus) return;

    try {
      await bookingApi.updateBooking(editingBooking.id, { status: newStatus });
      toast.success('Booking status updated');
      setEditingBooking(null);
      setNewStatus('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const filteredBookings = selectedBusiness
    ? bookings.filter((b) => b.businessId === selectedBusiness)
    : bookings;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-secondary-900">Bookings</h1>
        
        <select
          value={selectedBusiness}
          onChange={(e) => setSelectedBusiness(e.target.value)}
          className="input max-w-xs"
        >
          <option value="">All Businesses</option>
          {businesses.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>

      {filteredBookings.length > 0 ? (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase">
                  Business
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {filteredBookings.map((booking) => (
                <tr key={booking.id}>
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-secondary-900">
                      {booking.service?.name}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-sm text-secondary-500">
                    {booking.business?.name}
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-secondary-900">
                      {booking.user?.firstName} {booking.user?.lastName}
                    </p>
                    <p className="text-xs text-secondary-500">{booking.user?.email}</p>
                  </td>
                  <td className="px-6 py-4 text-sm text-secondary-500">
                    {new Date(booking.date).toLocaleDateString()} at {booking.time}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        statusColors[booking.status]
                      }`}
                    >
                      {booking.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => {
                        setEditingBooking(booking);
                        setNewStatus(booking.status);
                      }}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card p-12 text-center">
          <p className="text-secondary-500">No bookings found.</p>
        </div>
      )}

      {/* Status Update Modal */}
      <Modal
        isOpen={!!editingBooking}
        onClose={() => {
          setEditingBooking(null);
          setNewStatus('');
        }}
        title="Update Booking Status"
      >
        <div className="space-y-4">
          <div>
            <label className="label">Status</label>
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="input"
            >
              <option value="PENDING">Pending</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="COMPLETED">Completed</option>
              <option value="NO_SHOW">No Show</option>
            </select>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={() => {
                setEditingBooking(null);
                setNewStatus('');
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button onClick={handleStatusUpdate} className="btn-primary">
              Update Status
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
